import moment from 'moment';
import * as React from 'react';
import { MASK_PRESETS, MaskedInput } from '../../../components-v2/MaskedInput';
import { bs } from '../../../styles/styles';

const DATE_FORMAT = 'MM/DD/YYYY';

/**
 * Inline masked date input for the Schedule Blog Post footer. Shows the currently
 * selected date and lets the user type a new one. Emits a parsed Date via onDateChange
 * only when the entry is a complete, valid date; reports validity on every change so the
 * caller can gate the Schedule button. Syncs its text back in when selectedDate changes
 * from outside (e.g. a calendar day click).
 */
export function ScheduleDateInput({
	selectedDate,
	onDateChange,
	onValidityChange,
}: {
	selectedDate: Date;
	onDateChange: (date: Date) => void;
	/** May be an inline callback — the component captures it via ref so an unstable caller cannot cause a revalidation loop. */
	onValidityChange: (isValid: boolean) => void;
}) {
	const formattedSelectedDate = moment(selectedDate).format(DATE_FORMAT);
	const [value, setValue] = React.useState(formattedSelectedDate);

	// Keep the latest onValidityChange without making it an effect dependency, so an
	// inline (unstable) callback from a future caller can't re-run the sync effect every
	// render and stomp the draft mid-typing.
	const onValidityChangeRef = React.useRef(onValidityChange);
	onValidityChangeRef.current = onValidityChange;

	// Sync in when selectedDate changes from outside (calendar click). Keyed only on the
	// formatted date, so in-progress typing — which hasn't changed selectedDate — is never
	// clobbered. Re-marks valid so a prior invalid entry stops gating the Schedule button.
	React.useEffect(() => {
		setValue(formattedSelectedDate);
		onValidityChangeRef.current(true);
	}, [formattedSelectedDate]);

	return (
		<MaskedInput
			{...MASK_PRESETS.dateMDY}
			value={value}
			onValueChange={change => {
				setValue(change.formatted);
				onValidityChange(change.isValid);
				if (change.isValid) {
					onDateChange(moment(change.formatted, DATE_FORMAT, true).toDate());
				}
			}}
			styles={[bs.w40]}
		/>
	);
}
